import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog16 = () => {
  const hashtags = [
    "#emotionalintelligence",
    "#selfawareness",
    "#innerpeace",
    "#growthmindset",
    "#paramlife",
    "#mindfulness",
    "#selfmastery",
    "#consciousliving",
    "#mentalclarity",
    "#wisdom",
  ];

  return (
    <>
      <Helmet>
        <title>
          7 Things Emotionally Intelligent People Don’t Do (And Why You Should
          Learn From Them)
        </title>
        <meta
          name="description"
          content="Discover 7 things emotionally intelligent people don’t do — and how emotional balance, awareness, and calm can change your life from within."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          7 Things Emotionally Intelligent People Don’t Do
          <br />
          (And Why You Should Learn From Them)
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog16.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10 ml-10"
        />

        {/* Intro */}
        <section className="space-y-4 mb-8">
          <p>
            We live in a time when everyone talks about success, hustle, and
            productivity — but very few talk about emotional intelligence.
          </p>

          <p>It’s strange, isn’t it?</p>

          <p>
            We spend years learning how to build careers, but not how to build
            calm.
            <br />
            We study how to win arguments, but not how to win peace.
          </p>

          <p>
            And that’s why so many of us end up successful on paper, but restless
            inside.
          </p>

          <p className="text-[#578d48] font-semibold">
            Emotional intelligence — or EQ — is not about being emotionless.
          </p>

          <p>
            It’s about being emotionally aware and balanced.
            <br />
            It’s the difference between reacting and responding, between chaos
            and clarity.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are 7 things emotionally intelligent people don’t do — and how
            these simple shifts can change the way you live, love, and lead.
          </p>
        </section>

        {/* Point 1 */}
        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">
              1. They Don’t Get Trapped in Emotional Highs and Lows
            </h3>

            <p>
              Emotionally intelligent people don’t ride the rollercoaster of
              praise and criticism.
            </p>

            <p>
              When life gives them success, they don’t let it inflate their ego.
              <br />
              When life brings failure, they don’t let it break their spirit.
            </p>

            <p>
              They know both applause and judgment are temporary — so they stay
              rooted in who they are, not in what others say.
            </p>

            <p>
              In today’s world, we’re constantly pulled between likes, comments,
              and opinions. One compliment lifts us, one criticism ruins our day.
            </p>

            <p className="text-[#578d48]">
              But emotional maturity is learning to hold your center through
              both.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>Emotional balance is a superpower.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>True stability comes from within, not from shifting circumstances.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              Each time someone praises or criticizes you, pause and remind
              yourself:
            </p>
            <p className="text-[#578d48] italic">
              “This doesn’t define me. My peace is mine to protect.”
            </p>
          </div>

          {/* Point 2 */}
          <div>
            <h3 className="text-primary font-semibold">
              2. They Don’t Attach Their Identity to Outcomes
            </h3>

            <p>You are not your job title.</p>
            <p>You are not your grades, your follower count, or your paycheck.</p>

            <p>
              Emotionally intelligent people understand this deeply.
              <br />
              They focus on effort, not ego.
              <br />
              They let their actions express who they are — not their labels or
              results.
            </p>

            <p>
              In a world obsessed with numbers and validation, this mindset is
              revolutionary.
            </p>

            <p className="text-[#578d48]">
              Because when your self-worth depends on outcomes, you live in
              constant fear of failure.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>Self-esteem built on results will always feel fragile.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Let action be your offering, not your identity.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              At the end of each day, instead of asking, “Did I win?”, ask
              yourself:
            </p>
            <p className="text-[#578d48] italic">
              “Did I show up with integrity and give my best?”
            </p>
            <p>That’s success.</p>
          </div>

          {/* Point 3 */}
          <div>
            <h3 className="text-primary font-semibold">
              3. They Don’t Let External Chaos Disturb Their Inner Calm
            </h3>

            <p>
              Even when things fall apart around them, emotionally intelligent
              people don’t fall apart within.
            </p>

            <p>
              They practice presence.
              <br />
              They know peace isn’t something you wait for — it’s something you
              protect.
            </p>

            <p>
              When the world feels noisy, they come back to their breath.
              <br />
              When life feels uncertain, they return to the present moment.
            </p>

            <p className="text-[#578d48]">
              Because control is an illusion.
              <br />
              The only thing you can truly control is your response.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>You can’t control the world, but you can learn to control your response.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Be like the ocean — deep, vast, unshaken by surface waves.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              The next time chaos hits, take one deep breath before reacting.
              <br />
              You’ll be surprised how much power that one pause holds.
            </p>
          </div>

          {/* Point 4 */}
          <div>
            <h3 className="text-primary font-semibold">
              4. They Don’t Avoid Self-Reflection
            </h3>

            <p>
              Most people run away from silence — because silence shows us what
              we’ve been avoiding.
            </p>

            <p className="text-[#578d48]">
              But emotionally intelligent people make time for reflection.
            </p>

            <p>
              They don’t just blame others or the world.
              <br />
              They pause and ask:
            </p>

            <p className="italic">“What’s my part in this?”</p>
            <p className="italic">“What pattern am I repeating?”</p>

            <p>
              They know that without self-awareness, growth is impossible.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>Avoiding inner work keeps you stuck.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Know thyself — your mind, your patterns, your purpose.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              Before going to bed, ask yourself one question:
            </p>
            <p className="text-[#578d48] italic">
              “Did I act from awareness today or from autopilot?”
            </p>
            <p>Small self-checks like this build deep transformation.</p>
          </div>

          {/* Point 5 */}
          <div>
            <h3 className="text-primary font-semibold">
              5. They Don’t Seek Validation from Others
            </h3>

            <p>
              We all want to be seen and appreciated — but emotionally
              intelligent people don’t depend on external approval to feel good
              about themselves.
            </p>

            <p>
              They don’t adjust their truth just to be liked.
              <br />
              They don’t shrink themselves to fit in.
              <br />
              They validate themselves from within, through self-respect.
            </p>

            <p className="text-[#578d48]">
              Because they know — the need for validation is a subtle form of
              emotional dependence.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>Needing approval from others is emotional dependence.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Walk your own path even if no one claps for you.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              Each time you crave someone’s validation, ask:
            </p>
            <p className="text-[#578d48] italic">
              “Would I still do this if no one praised me for it?”
            </p>
            <p>
              If the answer is yes — you’re living from alignment, not approval.
            </p>
          </div>

          {/* Point 6 */}
          <div>
            <h3 className="text-primary font-semibold">
              6. They Don’t React Impulsively
            </h3>

            <p>
              Emotionally intelligent people don’t let emotions take the
              steering wheel.
            </p>

            <p>
              When something triggers them, they pause, breathe, and then
              respond consciously.
            </p>

            <p className="text-[#578d48]">
              They understand that emotional control doesn’t mean suppressing
              feelings — it means expressing them wisely.
            </p>

            <p>
              Every reaction you regret once began as an impulse.
            </p>

            <p className="text-[#578d48]">
              The difference between peace and regret often lies in one deep
              breath.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>Every reaction you regret began as an impulse.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Pause is power. Control over self is true strength.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              When you feel triggered, silently count “1… 2… 3…” before
              speaking.
              <br />
              That tiny pause can prevent massive emotional damage.
            </p>
          </div>

          {/* Point 7 */}
          <div>
            <h3 className="text-primary font-semibold">
              7. They Don’t Try to Fix Others to Avoid Their Own Work
            </h3>

            <p>
              This is one of the most common emotional traps — focusing on fixing
              others so we can avoid fixing ourselves.
            </p>

            <p className="text-[#578d48]">
              Emotionally intelligent people don’t fall for it.
            </p>

            <p>
              They understand that healing isn’t about changing others — it’s
              about aligning yourself.
            </p>

            <p>
              When you’re centered, your energy uplifts others effortlessly.
            </p>

            <p className="text-[#578d48]">
              But when you’re uncentered, even your help comes from a place of
              control.
            </p>

            <p className="text-[#578d48] font-semibold">Modern meaning:</p>
            <p>You can’t pour from an empty cup.</p>

            <p className="text-[#578d48] font-semibold">Wisdom lens:</p>
            <p>Heal yourself, and you uplift others without trying.</p>

            <p className="text-[#578d48] font-semibold">Try this:</p>
            <p>
              When you feel the urge to “help” someone, pause and ask:
            </p>
            <p className="text-[#578d48] italic">
              “Am I doing this from peace or from avoidance?”
            </p>
            <p>True compassion begins with self-honesty.</p>
          </div>
        </section>

        {/* Final Thoughts */}
        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            🪷 Final Thoughts: The Calm Is the Real Power
          </h2>

          <p>
            Being emotionally intelligent doesn’t mean you never feel anger,
            sadness, or fear.
          </p>

          <p className="text-[#578d48]">
            It means you know how to meet these emotions with awareness, not
            avoidance.
          </p>

          <p>
            It’s not about staying positive all the time.
          </p>

          <p className="text-[#578d48]">
            It’s about staying conscious, even in negativity.
          </p>

          <p className="text-[#578d48]">
            And when you master that — no matter what life throws at you — you
            remain grounded, graceful, and unshaken.
          </p>

          <p>
            So next time life tests your patience, triggers your emotions, or
            throws chaos your way…
          </p>

          <p>
            Don’t ask, “Why is this happening to me?”
            <br />
            Ask, “What is this teaching me about myself?”
          </p>

          <p className="text-[#578d48] font-semibold">
            That’s emotional intelligence in action.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog16;
